import * as React from 'react';
import { ChevronUp } from 'lucide-react';

import { cn } from '../lib/utils';
import { Button } from '../primitives/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../primitives/drawer';
import {
  intentToButtonVariant,
  visibleActions,
  type ActionDefinition,
} from './action-definition';

export interface ResponsiveChromeProps {
  /** Page title — the single H1. */
  title: string;
  /** Optional subtitle under the title. */
  subtitle?: React.ReactNode;
  /** Breadcrumb slot rendered above the title (e.g. the domain Breadcrumb). */
  breadcrumb?: React.ReactNode;
  /** Metadata pills next to the title (status, counts, freshness). */
  meta?: React.ReactNode;
  /** Page actions — inline toolbar >= md, pinned bottom action bar below. */
  actions?: ActionDefinition[];
  /** Page body. */
  children: React.ReactNode;
  className?: string;
}

/**
 * ResponsiveChrome — the page-scaffold archetype.
 *
 * - Sticky compact header: breadcrumb slot, title/subtitle, metadata pills.
 *   Vertical padding collapses one step under `[data-density='touch']`.
 * - Actions: an inline toolbar in the header from the md breakpoint; below,
 *   a bottom bar pinned over the safe-area with the primary action inline
 *   and the rest in a swipe-up action sheet.
 */
export function ResponsiveChrome({
  title,
  subtitle,
  breadcrumb,
  meta,
  actions = [],
  children,
  className,
}: ResponsiveChromeProps) {
  const shown = visibleActions(actions);
  const primary = shown.find((a) => a.intent === 'primary') ?? shown[0];
  const overflow = shown.filter((a) => a !== primary);
  const [sheetOpen, setSheetOpen] = React.useState(false);

  const runAction = (action: ActionDefinition) => {
    setSheetOpen(false);
    action.onSelect();
  };

  return (
    <div className={cn('flex min-h-0 flex-1 flex-col', className)} data-testid="responsive-chrome">
      <header
        className={cn(
          'sticky top-0 z-20 border-b border-border/60 bg-surface-base/90 backdrop-blur',
          'px-4 py-3 [[data-density=touch]_&]:py-2',
        )}
      >
        {breadcrumb && <div className="mb-1 text-xs">{breadcrumb}</div>}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="truncate font-heading text-lg font-semibold tracking-tight text-foreground">
              {title}
            </h1>
            {subtitle && (
              <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {meta}
            {shown.length > 0 && (
              <div
                className="hidden items-center gap-2 md:flex"
                role="toolbar"
                aria-label="Page actions"
              >
                {shown.map((a) => (
                  <Button
                    key={a.id}
                    size="sm"
                    variant={intentToButtonVariant(a.intent)}
                    onClick={a.onSelect}
                    disabled={a.disabled}
                  >
                    {a.icon && <span className="mr-1 inline-flex">{a.icon}</span>}
                    {a.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      <div className={cn('min-h-0 flex-1', shown.length > 0 && 'pb-16 md:pb-0')}>
        {children}
      </div>

      {shown.length > 0 && (
        <div
          className={cn(
            'fixed inset-x-0 bottom-0 z-30 flex items-center gap-2 md:hidden',
            'border-t border-border/60 bg-surface-raised/95 px-4 pt-2 backdrop-blur',
            'pb-[max(0.5rem,env(safe-area-inset-bottom))]',
          )}
          data-testid="responsive-chrome-action-bar"
        >
          {primary && (
            <Button
              className="flex-1"
              variant={intentToButtonVariant(primary.intent ?? 'primary')}
              onClick={primary.onSelect}
              disabled={primary.disabled}
            >
              {primary.icon && <span className="mr-1 inline-flex">{primary.icon}</span>}
              {primary.label}
            </Button>
          )}
          {overflow.length > 0 && (
            <Drawer open={sheetOpen} onOpenChange={setSheetOpen}>
              <DrawerTrigger asChild>
                <Button variant="outline" aria-label="More actions">
                  <ChevronUp className="h-4 w-4" aria-hidden />
                  More
                </Button>
              </DrawerTrigger>
              <DrawerContent>
                <DrawerHeader>
                  <DrawerTitle>{title} actions</DrawerTitle>
                </DrawerHeader>
                <div
                  className="flex flex-col gap-2 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
                  role="group"
                  aria-label="Page actions"
                >
                  {overflow.map((a) => (
                    <Button
                      key={a.id}
                      size="lg"
                      variant={
                        a.intent === 'destructive' ? 'destructive' : 'outline'
                      }
                      className="justify-start"
                      onClick={() => runAction(a)}
                      disabled={a.disabled}
                    >
                      {a.icon && <span className="mr-2 inline-flex">{a.icon}</span>}
                      {a.label}
                    </Button>
                  ))}
                </div>
              </DrawerContent>
            </Drawer>
          )}
        </div>
      )}
    </div>
  );
}

ResponsiveChrome.displayName = 'ResponsiveChrome';
